'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, Plus, CreditCard, CalendarDays, Key, Trash2 } from 'lucide-react'
import { db, GiftCard as DbGiftCard } from '../lib/db'

export function GiftCardsPage() {
  const [cards, setCards] = useState<DbGiftCard[]>(() => db.getGiftCards())
  const [search, setSearch] = useState('')
  const [showNew, setShowNew] = useState(false)

  // Creation fields
  const [code, setCode] = useState('')
  const [amount, setAmount] = useState(50000)
  const [buyerName, setBuyerName] = useState('')
  const [buyerEmail, setBuyerEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [validityMonths, setValidityMonths] = useState(6)

  const filtered = useMemo(() => {
    if (!search) return cards
    return cards.filter(c => c.code.toLowerCase().includes(search.toLowerCase()) || c.buyerName.toLowerCase().includes(search.toLowerCase()) || c.recipientName.toLowerCase().includes(search.toLowerCase()))
  }, [cards, search])

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let generated = ''
    for (let i = 0; i < 8; i++) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setCode(generated)
  }

  const handleCreate = () => {
    if (!code || !amount || !buyerName || !recipientName) return

    const newCard: DbGiftCard = {
      id: `gc-${Date.now()}`,
      code: code.toUpperCase(),
      amount,
      balance: amount,
      buyerName,
      buyerEmail: buyerEmail || `${buyerName.toLowerCase().replace(' ', '')}@gmail.com`,
      recipientName,
      expiresAt: new Date(Date.now() + validityMonths * 30 * 24 * 60 * 60 * 1000).toISOString(),
      isActive: true,
      createdAt: new Date().toISOString()
    }

    const updated = [newCard, ...cards]
    setCards(updated)
    db.saveGiftCards(updated)
    setShowNew(false)

    setCode('')
    setAmount(50000)
    setBuyerName('')
    setBuyerEmail('')
    setRecipientName('')
  }

  const deleteCard = (id: string) => {
    const updated = cards.filter(c => c.id !== id)
    setCards(updated)
    db.saveGiftCards(updated)
  }

  const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Tarjetas de Regalo (Gift Cards)</h1>
          <p className="text-xs text-slate-500">{cards.length} gift cards registradas en el sistema</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          <Plus className="h-4 w-4" /> Emitir Gift Card
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por código, comprador o destinatario..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(card => (
            <div key={card.id} className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-850 rounded-2xl shadow-xl border border-slate-800 p-5 flex flex-col justify-between text-white group h-48">
              <div className="absolute top-0 right-0 h-24 w-24 bg-cyan-500/10 rounded-bl-full pointer-events-none" />
              <div>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-cyan-400" />
                    <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Innovaderm SPA</span>
                  </div>
                  <button onClick={() => deleteCard(card.id)} className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-rose-400 transition">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="mt-4">
                  <div className="text-xl font-mono tracking-widest font-extrabold text-cyan-300">{card.code}</div>
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 font-medium">Para:</span>
                    <span className="text-xs text-white font-bold">{card.recipientName}</span>
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-3 border-t border-white/5 flex items-end justify-between">
                <div>
                  <span className="text-[9px] text-slate-400 block uppercase font-semibold leading-none">Saldo</span>
                  <span className="text-base font-extrabold text-emerald-400 mt-1 block">{fmtCLP(card.balance)}</span>
                </div>
                <div className="text-right">
                  <span className="text-[9px] text-slate-400 block uppercase font-semibold leading-none">Expiración</span>
                  <span className="text-[10px] text-slate-300 font-medium mt-1 block">{new Date(card.expiresAt).toLocaleDateString('es-CL')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-4">Emitir Nueva Gift Card</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Código Único *</label>
                <div className="flex gap-2">
                  <input type="text" placeholder="Ej: REGALOMAMA" className="flex-1 h-9 border border-slate-200 rounded-lg px-3 text-sm font-mono uppercase" value={code} onChange={e => setCode(e.target.value)} />
                  <button onClick={generateCode} className="h-9 px-3 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1">
                    <Key className="h-3.5 w-3.5" /> Generar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Monto total ($) *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={amount} onChange={e => setAmount(Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Validez (meses) *</label>
                  <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={validityMonths} onChange={e => setValidityMonths(Number(e.target.value))}>
                    <option value={3}>3 meses</option>
                    <option value={6}>6 meses</option>
                    <option value={12}>12 meses</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre Destinatario *</label>
                <input type="text" placeholder="Ej: Marcela Lagos" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={recipientName} onChange={e => setRecipientName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre Comprador *</label>
                  <input type="text" placeholder="Ej: Javier Soto" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={buyerName} onChange={e => setBuyerName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Correo Comprador</label>
                  <input type="email" placeholder="correo@gmail.com" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreate} disabled={!code || !buyerName || !recipientName} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Emitir Tarjeta</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
